import * as Sentry from '@sentry/capacitor';
import { ReplaySubject } from 'rxjs';

import { localStorageManager } from '@mopla/utils';

const STORAGE_KEY = 'sentry_persisted_breadcrumbs';
const MAX_BREADCRUMBS = 50;

const breadcrumbsStorage = localStorageManager<Sentry.Breadcrumb[]>(
	STORAGE_KEY,
	sessionStorage
);

const breadcrumbs$ = new ReplaySubject<Sentry.Breadcrumb>(MAX_BREADCRUMBS);

/**
 * Collector for beforeBreadcrumb callback.
 * Saves breadcrumb to ReplaySubject buffer for subsequent persistence.
 */
export const collectBreadcrumb = (
	breadcrumb: Sentry.Breadcrumb
): Sentry.Breadcrumb => {
	breadcrumbs$.next({ ...breadcrumb });
	return breadcrumb;
};

/**
 * Save collected breadcrumbs to sessionStorage.
 * Call BEFORE redirecting to external site.
 */
export const persistBreadcrumbs = (): void => {
	try {
		Sentry.addBreadcrumb({
			category: 'app',
			message: 'Breadcrumbs saved to sessionStorage',
		});

		const breadcrumbs: Sentry.Breadcrumb[] = [];
		breadcrumbs$.subscribe((b) => breadcrumbs.push(b)).unsubscribe();

		if (breadcrumbs.length > 0) {
			breadcrumbsStorage.set(breadcrumbs);
		}
	} catch {
		/** sessionStorage may be unavailable */
	}
};

/**
 * Restore breadcrumbs from sessionStorage and add to Sentry.
 * Call during app initialization (in initSentry).
 */
export const restoreBreadcrumbs = (): void => {
	try {
		const saved = breadcrumbsStorage.get({ safe: true });

		if (Array.isArray(saved) && saved.length > 0) {
			saved.forEach((b) => Sentry.addBreadcrumb(b));
			breadcrumbsStorage.clear();

			Sentry.addBreadcrumb({
				category: 'app',
				message: 'Breadcrumbs restored from sessionStorage',
			});
		}
	} catch {
		/** ignore errors */
	}
};
